/**
 * Cookie Consent Utilities
 * GDPR/CCPA compliant cookie management functions
 */

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const COOKIE_CONSENT_NAME = 'cookie_consent_preferences';
export const COOKIE_CONSENT_EXPIRES_DAYS = 365; // 12 months

/**
 * Set a cookie with the given name, value, and expiration days
 */
export function setCookie(name: string, value: string, days: number): void {
  if (typeof window === 'undefined') return;
  
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null;
  
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return null;
}

/**
 * Delete a cookie by name
 */
export function deleteCookie(name: string): void {
  if (typeof window === 'undefined') return;
  
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

/**
 * Get cookie consent preferences
 */
export function getCookiePreferences(): CookiePreferences | null {
  const preferences = getCookie(COOKIE_CONSENT_NAME);
  
  if (!preferences) return null;
  
  try {
    return JSON.parse(preferences);
  } catch (error) {
    console.error('Error parsing cookie preferences:', error);
    return null;
  }
}

/**
 * Set cookie consent preferences
 */
export function setCookiePreferences(preferences: CookiePreferences): void {
  setCookie(
    COOKIE_CONSENT_NAME,
    JSON.stringify(preferences),
    COOKIE_CONSENT_EXPIRES_DAYS
  );
}

/**
 * Check if user has given consent for a specific cookie type
 */
export function hasConsent(type: keyof CookiePreferences): boolean {
  const preferences = getCookiePreferences();
  
  if (!preferences) return false;
  
  return preferences[type] === true;
}

/**
 * Default cookie preferences (essential cookies always true)
 */
export const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
};

/**
 * Accept all cookies preferences
 */
export const ACCEPT_ALL_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: true,
  marketing: true,
};

/**
 * Reject non-essential cookies preferences
 */
export const REJECT_NON_ESSENTIAL_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
};
